import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { ScenesData } from '@packages/global';
import { IProjectAsset } from '@packages/global';
import ItemCard from '../cards/ItemCard';

/**
 * ScenesStep 组件属性
 */
export interface ScenesStepProps {
  /** 步骤数据 */
  data: ScenesData | null;
  /** 脚本 ID */
  scriptId?: string;
  /** 项目 ID */
  projectId?: string;
  /** 集 ID */
  episodeId?: string;
  /** 艺术风格 */
  artStyle?: string;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 图片正在生成中（步骤 IN_PROGRESS），禁用卡片操作 */
  imageGenerating?: boolean;
  /** 来自 ProjectAsset 的主体列表，用于实时反映生成状态和图片 */
  episodeAssets?: IProjectAsset[];
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 数据更新回调 */
  onUpdate?: () => void;
}

/**
 * 空状态组件
 */
function EmptyState({ onRegenerate, t }: { onRegenerate?: () => void; t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-bg-elevated flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-gray-200 mb-2">{t('script:scenes.empty')}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t('script:scenes.emptyDesc')}
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('script:scenes.generate')}
        </button>
      )}
    </div>
  );
}

/**
 * ScenesStep 组件
 * 
 * 复用原有的 ItemCard 组件，支持：
 * - 图片上传/AI生成
 * - 删除场景
 * 
 * Requirements: 6.7
 */
export default function ScenesStep({
  data,
  scriptId,
  projectId,
  episodeId,
  artStyle,
  isLoading = false,
  imageGenerating = false,
  episodeAssets = [],
  onRegenerate,
  onUpdate,
}: ScenesStepProps) {
  const { t } = useTranslation(['script', 'common']);
  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">{t('script:scenes.generating')}</span>
        </div>
      </div>
    );
  }

  // 空状态
  if (!data || data.scenes.length === 0) {
    return <EmptyState onRegenerate={onRegenerate} t={t} />;
  }

  return (
    <div className="space-y-5">
      {/* 场景网格 - 固定宽度 200px，两端对齐 */}
      <div className="flex flex-wrap gap-4">
        {data.scenes.map((scene, index) => {
          const matchedAsset = episodeAssets.find(a => a.name === scene.name);
          return (
            <div key={index} className="w-[200px] flex-shrink-0">
              <ItemCard
                name={scene.name}
                imageUrl={scene.imageUrl}
                prompt={scene.prompt}
                description={scene.description}
                scriptId={scriptId || ''}
                type="scene"
                projectId={projectId}
                episodeId={episodeId}
                artStyle={artStyle}
                imageGenerating={imageGenerating}
                assetGenerationStatus={matchedAsset?.generationStatus}
                assetImageUrl={matchedAsset?.conceptImageUrl || matchedAsset?.frontViewUrl}
                matchedAsset={matchedAsset}
                onUpdate={onUpdate}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
